import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, Collapse } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import accountClockIcon from "@iconify/icons-mdi/account-clock";
import accountPlusIcon from "@iconify/icons-mdi/account-plus";
import cityIcon from "@iconify/icons-mdi/city";
import creditCardIcon from "@iconify/icons-mdi/credit-card-marker-outline";
import homeAccountIcon from "@iconify/icons-mdi/home-account";
import siteMapIcon from "@iconify/icons-mdi/sitemap";
import {
  MyHrContractCardItem,
  MyHrPaycardWrapper,
  MyHrPayrollCollapseHeader,
  MyHrPayrollMergedCollapseHeader,
} from "my-hr/components";
import { useMyHrPaycard } from "my-hr/hooks";
import { useOcClientState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface MyHrPayrollCollapseProps {
  label: string;
  ContractID: string;
  index: number;
  showStartOfContract: boolean;
  company: string;
  workingPlace: string;
  orgUnit: string;
  payOffice: string;
  startOfContract: string;
  employmentType: string;
  month: string;
  year: string;
  contract: string;
}

export const MyHrPayrollCollapse: React.FC<MyHrPayrollCollapseProps> = ({
  label,
  ContractID,
  index,
  showStartOfContract,
  company,
  workingPlace,
  orgUnit,
  payOffice,
  startOfContract,
  employmentType,
  month,
  year,
  contract,
}) => {
  const theme: Theme = useTheme();
  const { t } = useTranslation();
  const isMobile: boolean = useOcIsMobile();

  const [expanded, setExpanded] = useOcClientState<boolean>(
    `collapseExpanded${index}`,
    true,
  );
  const handleExpandClick = (): void => {
    setExpanded(!expanded);
  };

  const [mergedExpanded, setMergedExpanded] = useOcClientState<boolean>(
    `collapseMergedExpanded${index}`,
    false,
  );
  const handleMergedExpandClick = (): void => {
    setMergedExpanded(!mergedExpanded);
  };

  const { data } = useMyHrPaycard({ contract, year, month });

  const source: string[] = data?.paycards ?? [];
  const nonePaycard: boolean = source[0]?.includes("Lista") ?? false;

  const sourceMerge: string[] = data?.paycards?.slice(0, 1) ?? [];
  const sourceWithoutMerge: string[] = data?.paycards?.slice(1) ?? [];

  return (
    <Box>
      <MyHrPayrollCollapseHeader
        key={`collapseExpanded-${index}`}
        label={label}
        ContractID={ContractID}
        handleExpandClick={handleExpandClick}
        expanded={expanded}
      />
      <Collapse
        in={expanded}
        style={{
          backgroundColor: theme.palette.white.main,
          padding: "0px 16px",
        }}
      >
        <Box style={{ borderTop: `2px solid ${theme.palette.gray40.main}` }} />
        <Box height="24px" />
        <Box
          display="flex"
          flexGrow={1}
          width={isMobile ? "auto" : "696px"}
          margin="auto"
          flexDirection={isMobile ? "column" : undefined}
        >
          <Box display="flex" flexGrow={1}>
            <Box paddingTop="6px">
              <MyHrContractCardItem icon={cityIcon} label={company} />
              <Box height="10px" />
              <MyHrContractCardItem
                icon={homeAccountIcon}
                label={workingPlace}
              />
              <Box height="10px" />
              <MyHrContractCardItem icon={siteMapIcon} label={orgUnit} />
            </Box>
          </Box>
          <Box paddingTop="6px">
            <MyHrContractCardItem icon={creditCardIcon} label={payOffice} />
            <Box height="10px" />

            {showStartOfContract && (
              <MyHrContractCardItem
                icon={accountPlusIcon}
                label={new Date(startOfContract).toLocaleDateString()}
              />
            )}
            <Box height="10px" />
            <MyHrContractCardItem
              icon={accountClockIcon}
              label={employmentType}
            />
          </Box>
        </Box>
        <Box height="56px" />
        <Card
          style={{
            borderRadius: "8px",
            padding: isMobile ? undefined : "13px 100px",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? undefined : "center",
            overflowX: isMobile ? "scroll" : undefined,
            overflowY: isMobile ? "hidden" : undefined,
            whiteSpace: isMobile ? "nowrap" : undefined,
          }}
          elevation={0}
        >
          {nonePaycard ? (
            <Box>
              <span>{t("MY_HR_NO_PAYROLL_DATA")}</span>
            </Box>
          ) : (
            <Box width={isMobile ? "auto" : "921px"}>
              <MyHrPayrollMergedCollapseHeader
                key={`collapseMergedExpanded-${index}`}
                label={t("MY_HR_MERGED")}
                handleExpandClick={handleMergedExpandClick}
                expanded={mergedExpanded}
              />
              <Collapse in={mergedExpanded}>
                <Box
                  style={{
                    borderTop: `2px solid ${theme.palette.gray40.main}`,
                  }}
                />
                <MyHrPaycardWrapper
                  contract={ContractID}
                  source={sourceMerge}
                  collapse={true}
                  slot={1}
                />
              </Collapse>
              <Box height="36px" />
              <MyHrPaycardWrapper
                contract={ContractID}
                source={sourceWithoutMerge}
                collapse={false}
                slot={2}
              />
            </Box>
          )}
        </Card>
        <Box height="32px" />
      </Collapse>
      <Box height="16px" />
    </Box>
  );
};

export default MyHrPayrollCollapse;
